import {computed, inject, Injectable, Signal, signal} from '@angular/core'
import {BetalSkyddItems, getDefaultLoan, KapitalForsakringar, LivforsakringarItems} from '../../0-application/shared'
import {Loan} from '../../model/loan'
import {Account} from '../../model/account'
import {Configuration} from '../configuration'
import {Investment} from '../../model/investment'
import {Insurance} from '../../model/insurance'
import {ILoan, IOwner, IValuation} from '../../0-application/types'
import {Autogiro} from '../../model/autogiro'
import {Contribution} from '../../model/contribution'
import {Base} from '../../model/base'

/**
 * Helper functions to make some calculations. Many of them
 * possibly useless
 */
export const loanSum = (loans: Loan[]) => {
  return loans
    .filter(l => l.active())
    .reduce((acc, a) => acc + a.amount(), 0)
}

export const weightedInterest = (loans: Loan[]) => {
  return loans
    .filter(l => l.active())
    .reduce((sum, l) => sum + (l.amount() * l.interest()), 0) / loanSum(loans)
}

export const weightedDiscount = (loans: Loan[]) => {
  return loans
    .filter(l => l.active())
    .reduce((sum, l) => sum + (l.amount() * l.discount()), 0) / loanSum(loans)
}

/**
 * Filter "insurances" that is LEO products...
 */
const filterLeo = (insurances: Insurance[], filter: string[]) => {
  return insurances.filter(i => {
    return filter
      .reduce((a, c) => a || i.name().toLowerCase().includes(c.toLowerCase()), false)
  })
}

export const kapitalForsakringar = (insurances: Insurance[]) => {
  return filterLeo(insurances, KapitalForsakringar)
}

export const betalSkydd = (insurances: Insurance[]) => {
  return filterLeo(insurances, BetalSkyddItems)
}

export const livforsakring = (insurances: Insurance[]) => {
  return filterLeo(insurances, LivforsakringarItems)
}


/**
 * Minimum for a dummy service
 */
export interface ICalculus {
  ltvNew: Signal<number>
}

/**
 * Dummy to use when none is available
 */
export class DummyCalculus implements ICalculus {
  public ltvNew = signal(80).asReadonly()
}

/**
 * Use this service to transfer input values. And make
 * calculations for profitability.
 *
 * Do not use this service to fetch data.
 */
@Injectable({
  providedIn: 'root'
})
export class Calculus implements ICalculus {
  /**
   * The primary owner so that we can sort vigilantly. This is
   * the sub (personnummer).
   */
  public primary = signal<string>('')

  /**
   * The value of the property (id est House or apartment)
   */
  public propertyValue = signal<number>(0)

  public amortizationBasisDebt = signal<number>(0)
  public amortizationBasisValue = signal<number>(0)

  public selectedValuation = computed(() => {
    return this.valuations().find(v => v.marketValue === this.propertyValue())
  })
  /**
   * The new loan to use in calculations
   */
  public seekedAmount = signal<number>(0)

  /**
   * Autogiro for Investments/Abasec n * 36 * 0.0055
   */
  private readonly pMonthlyInvestments = signal<Autogiro[]>([])
  public monthlyInvestments = this.pMonthlyInvestments.asReadonly()

  public remainToDistribute = computed(() =>
    this.seekedAmount() - this.sumOfNewLoans() + this.uncoveredLoans())

  /**
   * The sum of loan amount for current loans that
   * are marked as inactive, contributes to left to distribute.
   */
  public uncoveredLoans = computed(() =>
    this.pCurrentLoans()
      .filter(l => !l.active())
      .reduce((sum, l) => sum + l.amount(), 0)
  )
  /**
   * The current loans as they stand when we start. This should
   * be a rather static list.
   */
  private readonly pCurrentLoans = signal<Loan[]>([])
  public readonly currentLoans = this.pCurrentLoans.asReadonly()
  /**
   * Add loans here. These are new (property) loans.
   */
  private readonly pNewLoans = signal<Loan[]>([])
  public newLoans = this.pNewLoans.asReadonly()

  /** All loans, existing and added */
  public allPropertyLoans = computed(() => this.pCurrentLoans().concat(this.newLoans()))

  public isBorgoCustomer = computed(() => this.pCurrentLoans().some(l => l.productId() === 'Borgo'))

  public allActivePropertyLoans = computed(() =>
    this.allPropertyLoans().filter(l => l.active()))

  public sumOfOldLoans = computed(() =>
    this.pCurrentLoans()
      .filter(l => l.active())
      .reduce((sum, l) => sum + l.amount(), 0))

  public sumOfNewLoans = computed(() =>
    this.newLoans()
      .reduce((sum, l) => sum + l.amount(), 0))

  public sumOfAllLoans = computed(() =>
    this.sumOfOldLoans() + this.sumOfNewLoans())

  /**
   * Ltv = Loan to Value, "Belåningsgrad"
   */
  public ltvNow = computed(() =>
    this.pCurrentLoans()
      .filter(l => l.active())
      .reduce((sum, l) => sum + l.amount(), 0) / this.propertyValue())

  public ltvNew = computed(() =>
    (this.sumOfOldLoans() + this.sumOfNewLoans()) / this.propertyValue())

  private readonly pInsurances = signal<Insurance[]>([])
  public insurances = this.pInsurances.asReadonly()

  private readonly pInvestments = signal<Investment[]>([])
  public investments = this.pInvestments.asReadonly()

  private readonly pContributions = signal<Contribution[]>([])
  public contributions = this.pContributions.asReadonly()

  private readonly pValuations = signal<IValuation[]>([])
  public valuations = this.pValuations.asReadonly()


  /**
   * Owners are just a stupid list of less meaning and (currently)
   * posses no real logic.
   */
  public owners = signal<IOwner[]>([])

  public ownerIds = computed(() => this.owners().map(o => o.sub))

  private readonly pPrivateLoans = signal<Account[]>([])

  public privateLoans = this.pPrivateLoans.asReadonly()

  private readonly pOtherAccounts = signal<Account[]>([])
  public otherAccounts = this.pOtherAccounts.asReadonly()

  public loanSummaries = computed(() => {
    return this.newLoans().map(l => l.summarize())
      .concat(this.privateLoans()
        .filter(a => a.isNew).map(a => a.summarize()))
  })

  private readonly configuration = inject(Configuration)

  /**
   * Empty all lists and reset others as needed.
   */
  public reset() {
    this.pCurrentLoans.set([])
    this.pNewLoans.set([])
    this.pOtherAccounts.set([])
    this.pPrivateLoans.set([])
    this.pInsurances.set([])
    this.pInvestments.set([])
    this.owners.set([])
    this.pMonthlyInvestments.set([])
    this.pContributions.set([])
    this.pValuations.set([])
  }

  /**
   * Loans can only be added, never removed.
   */
  public addOldPropertyLoans(loans: Loan[]) {
    loans.forEach(loan => {
      loan.calculus.set(this)
      loan.configuration.set(this.configuration)
    })
    this.pCurrentLoans.update(current => current.concat(loans))
  }

  /**
   * Make sure to only add _loans_ here. If you accidentaly add other
   * accounts it will be mayhem.
   * @param loans
   */
  public addPrivateLoans(loans: Account[]) {
    loans.forEach(loan => loan.configuration.set(this.configuration))
    this.pPrivateLoans.update(current => current.concat(loans))
  }

  public addOtherAccounts(loans: Account[]) {
    this.pOtherAccounts.update(current => current.concat(loans))
  }

  public addInsurances(insurances: Insurance[]) {
    this.pInsurances.update(current => current.concat(insurances))
  }

  public addInvestments(investments: Investment[]) {
    this.pInvestments.update(current => current.concat(investments))
  }

  public addAutogiro(autogiros: Autogiro[]) {
    this.pMonthlyInvestments.update(current => current.concat(autogiros))
  }

  public addContribution(contributions: Contribution[]) {
    this.pContributions.update(current => current.concat(contributions))
  }

  public addValuation(valuation: IValuation[]) {
    // Put all in a map to avoid duplicates
    const map = new Map<number, IValuation>(
      this.pValuations()
        .concat(valuation.filter(Boolean))
        .map(v => [v.collateralId, v])
    )
    this.pValuations.set([...map.values()])
  }

  /**
   * Adds a new loan based on what is left to
   * distribute or the provided amount and calculates average interest
   * based on current loans.
   *
   * Must be anonymous since we pass it to a component intput
   */
  public addNewLoan = () => {
    const input = getDefaultLoan(
      this.remainToDistribute(),
      weightedDiscount(this.pCurrentLoans()) * -1
    )
    const loan = new Loan(input)
    loan.productId.set(this.isBorgoCustomer() ? 'Borgo' : 'l753')
    loan.isNew = true
    loan.calculus.set(this)
    loan.configuration.set(this.configuration)
    loan.accountNo = crypto.randomUUID()
    this.pNewLoans.update(l => l.concat(loan))
  }

  public setNewLoans(loans: ILoan[]) {
    this.pNewLoans.set(
      loans
        .filter(Boolean)
        .map(l => {
          const loan = new Loan(l)
          loan.calculus.set(this)
          loan.configuration.set(this.configuration)
          return loan
        }))
  }

  public deleteNewLoan(loan: Loan) {
    this.pNewLoans.update(current => current.filter(l => l !== loan))
  }

  public deleteItem(item: Base<any>) {
    this.pInsurances.update(current => current.filter(l => l !== item))
    this.pInvestments.update(current => current.filter(l => l !== item))
    this.pOtherAccounts.update(current => current.filter(l => l !== item))
    this.pPrivateLoans.update(current => current.filter(l => l !== item))
    this.pContributions.update(current => current.filter(l => l !== item))
  }

  /**
   * Take a list of any sort and return it split per user.
   * First item is considered primary.
   */
  public listToOwners(list: Base<any>[]) {
    const map = new Map<string, Base<any>[]>()
    this.owners().forEach(owner => {
      map.set(owner.sub, [])
    })
    list.forEach(base => {
      const bs = map.get(base.owner) ?? []
      map.set(base.owner, bs.concat(base))
    })
    return [...map.values()]
  }
}
